/**
 * Form controls: the labelled field, text input, select, textarea, the
 * switch/toggle, the checkbox and the filter chip. 36 px controls, radius 8;
 * focus is the accent edge plus a 3 px accent-glow ring. Re-exported from
 * `Ui.tsx`; screens import from there.
 */
import type { CSSProperties, InputHTMLAttributes, ReactElement, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { Children, cloneElement, forwardRef, isValidElement, useEffect, useId, useRef } from 'react'
import { Check, ChevronDown, CircleAlert, Minus } from 'lucide-react'

import { cx } from './primitives'
import styles from './Form.module.css'

/* ------------------------------------------------------------------------ */
/* input                                                                    */
/* ------------------------------------------------------------------------ */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Figures, codes, hashes: JetBrains Mono with tabular numerals. */
  mono?: boolean
  /** 32 px instead of 36 (toolbars, table rows). */
  compact?: boolean
  /** Red edge and ring; Field sets it by itself when it shows an error. */
  invalid?: boolean
  /** Icon inside the left edge, e.g. `<Search />`. */
  leading?: ReactNode
  /** A unit or a small button inside the right edge, e.g. «USDT». */
  trailing?: ReactNode
}

/** The text field (36 px, radius 8, accent focus ring); `leading`/`trailing` put an icon or a unit inside the frame. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, style, mono = false, compact = false, invalid, leading, trailing, ...rest },
  ref,
): JSX.Element {
  const ariaInvalid = invalid ?? rest['aria-invalid']
  if (leading === undefined && trailing === undefined) {
    return (
      <input
        ref={ref}
        className={cx(styles.control, styles.input, mono ? styles.mono : '', compact ? styles.compact : '', className)}
        style={style}
        {...rest}
        aria-invalid={ariaInvalid || undefined}
      />
    )
  }
  return (
    <span
      className={cx(styles.affix, compact ? styles.compact : '', rest.disabled ? styles.affixDisabled : '', className)}
      style={style}
      data-invalid={ariaInvalid ? '' : undefined}
    >
      {leading ? <span className={styles.leading}>{leading}</span> : null}
      <input ref={ref} className={cx(styles.affixInput, mono ? styles.mono : '')} {...rest} aria-invalid={ariaInvalid || undefined} />
      {trailing ? <span className={styles.trailing}>{trailing}</span> : null}
    </span>
  )
})

/* ------------------------------------------------------------------------ */
/* select                                                                   */
/* ------------------------------------------------------------------------ */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  mono?: boolean
  compact?: boolean
  invalid?: boolean
}

/** The native select in the kit's frame with a chevron; `className`/`style` size the frame. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, style, mono = false, compact = false, invalid, ...rest },
  ref,
): JSX.Element {
  const ariaInvalid = invalid ?? rest['aria-invalid']
  return (
    <span className={cx(styles.selectWrap, compact ? styles.compact : '', rest.disabled ? styles.selectDisabled : '', className)} style={style}>
      <select
        ref={ref}
        className={cx(styles.control, styles.select, mono ? styles.mono : '', compact ? styles.compact : '')}
        {...rest}
        aria-invalid={ariaInvalid || undefined}
      />
      <ChevronDown className={styles.chevron} aria-hidden="true" />
    </span>
  )
})

/* ------------------------------------------------------------------------ */
/* textarea                                                                 */
/* ------------------------------------------------------------------------ */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean
  invalid?: boolean
}

/** A multi-line field (min 88 px, grows by the operator's drag). */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ className, mono = false, invalid, ...rest }, ref): JSX.Element {
  const ariaInvalid = invalid ?? rest['aria-invalid']
  return <textarea ref={ref} className={cx(styles.control, styles.textarea, mono ? styles.mono : '', className)} {...rest} aria-invalid={ariaInvalid || undefined} />
})

/* ------------------------------------------------------------------------ */
/* field                                                                    */
/* ------------------------------------------------------------------------ */

export interface FieldProps {
  /** The label above the control (12/16, medium). */
  label: ReactNode
  /** One line of help under the control; hidden while `error` shows. */
  hint?: ReactNode
  /** Replaces the hint in red and marks the control invalid. */
  error?: ReactNode
  /** Small text right of the label, e.g. «необязательно» or a counter. */
  aside?: ReactNode
  className?: string
  children: ReactNode
}

const FIELD_CONTROLS = new Set<unknown>([Input, Select, Textarea])

/**
 * A label, its control and a hint or error. It is a <label>, so a click on
 * the text focuses the control; a kit Input/Select/Textarea child is marked
 * invalid and described by the hint/error automatically.
 */
export function Field({ label, hint, error, aside, className, children }: FieldProps): JSX.Element {
  const noteId = useId()
  const hasError = error !== null && error !== undefined && error !== false && error !== ''
  const note = hasError ? error : hint
  const only = Children.count(children) === 1 ? Children.only(children) : null
  const control =
    only !== null && isValidElement(only) && FIELD_CONTROLS.has(only.type)
      ? cloneElement(only as ReactElement<Record<string, unknown>>, {
          'aria-describedby': note ? noteId : undefined,
          invalid: hasError || (only.props as { invalid?: boolean }).invalid,
        })
      : children
  return (
    <label className={cx(styles.field, className)}>
      <span className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {aside ? <span className={styles.aside}>{aside}</span> : null}
      </span>
      {control}
      {hasError ? (
        <span id={noteId} className={styles.error}>
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
        </span>
      ) : hint ? (
        <span id={noteId} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </label>
  )
}

/* ------------------------------------------------------------------------ */
/* switch / toggle                                                          */
/* ------------------------------------------------------------------------ */

export interface SwitchProps {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Accessible name (the switch has no visible text). */
  label: string
  /** sm: 28×16 for dense rows. */
  size?: 'sm' | 'md'
  className?: string
}

/** A bare on/off switch (36×20, the thumb slides in 200 ms); `label` is its aria-label. */
export function Switch({ checked, onChange, disabled, label, size = 'md', className }: SwitchProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={cx(styles.switch, size === 'sm' ? styles.switchSm : '', className)}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.thumb} aria-hidden="true" />
    </button>
  )
}

export interface ToggleProps {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Visible text beside the switch (also its accessible name). */
  label: ReactNode
  /** A second, quieter line under the label. */
  description?: ReactNode
  /** Put the switch after the text (settings rows). */
  switchLast?: boolean
  className?: string
}

/** A switch with its visible label (and optional description); the whole row toggles. */
export function Toggle({ checked, onChange, disabled, label, description, switchLast = false, className }: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={cx(styles.toggle, switchLast ? styles.toggleLast : '', className)}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.switch} aria-hidden="true">
        <span className={styles.thumb} />
      </span>
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        {description ? <span className={styles.toggleDescription}>{description}</span> : null}
      </span>
    </button>
  )
}

/* ------------------------------------------------------------------------ */
/* checkbox                                                                 */
/* ------------------------------------------------------------------------ */

export interface CheckboxProps {
  checked: boolean
  onChange: (next: boolean) => void
  /** Visible text; without it pass `ariaLabel`. */
  label?: ReactNode
  description?: ReactNode
  /** The «some selected» dash (a header checkbox over a list). */
  indeterminate?: boolean
  disabled?: boolean
  ariaLabel?: string
  name?: string
  className?: string
}

/** A native checkbox in the kit's 16 px box (accent fill, the tick pops in); keyboard and forms work as usual. */
export function Checkbox({ checked, onChange, label, description, indeterminate = false, disabled, ariaLabel, name, className }: CheckboxProps): JSX.Element {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <label className={cx(styles.check, disabled ? styles.checkDisabled : '', label ? '' : styles.checkBare, className)}>
      <input
        ref={ref}
        type="checkbox"
        className={styles.checkInput}
        checked={checked}
        disabled={disabled}
        name={name}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.box} aria-hidden="true" data-state={indeterminate ? 'mixed' : checked ? 'on' : 'off'}>
        {indeterminate ? <Minus /> : <Check />}
      </span>
      {label ? (
        <span className={styles.checkText}>
          <span className={styles.checkLabel}>{label}</span>
          {description ? <span className={styles.toggleDescription}>{description}</span> : null}
        </span>
      ) : null}
    </label>
  )
}

/* ------------------------------------------------------------------------ */
/* chip                                                                     */
/* ------------------------------------------------------------------------ */

export interface ChipProps {
  pressed: boolean
  onClick: () => void
  children: ReactNode
  /** Leading icon (a tick is shown when pressed and no icon is given). */
  icon?: ReactNode
  disabled?: boolean
  className?: string
  style?: CSSProperties
}

/** A toggle pill for multi-select filters (aria-pressed; accent wash when on). */
export function Chip({ pressed, onClick, children, icon, disabled, className, style }: ChipProps): JSX.Element {
  return (
    <button type="button" aria-pressed={pressed} className={cx(styles.chip, className)} onClick={onClick} disabled={disabled} style={style}>
      {icon ? <span className={styles.chipIcon}>{icon}</span> : pressed ? <Check className={styles.chipTick} aria-hidden="true" /> : null}
      {children}
    </button>
  )
}
